import { Link } from "react-router";
import type { EntryView } from "../api/types.js";
import { formatTime, excerpt } from "../lib/format.js";
import { isMine, showsReadReceipt, visibilityLabel } from "../lib/entries.js";
import { Avatar } from "./Avatar.js";
import { CheckIcon, LockIcon } from "./icons.js";

/**
 * 列表里的一张日记卡。
 *
 * 刻意**不显示回应条数**：`/api/entries` 的响应里没有它，凑这个数字就得为
 * 每张卡再发一个请求。宁可少一个装饰，也不为一个数字制造 N 次往返。
 *
 * 「TA 已读」只出现在「我写的共享日记」上，且只在对方读过时出现 ——
 * **绝不显示「未读」**（spec §5.4.4）。
 */
export function EntryCard({
  entry,
  meId,
}: {
  entry: EntryView;
  meId: string;
}) {
  const mine = isMine(entry, meId);
  const read = showsReadReceipt(entry, meId);

  return (
    <Link
      to={`/entries/${entry.id}`}
      className={mine ? "entry-card me" : "entry-card ta"}
    >
      <div className="entry-head">
        <Avatar
          name={entry.authorUsername}
          variant={mine ? "me" : "ta"}
          size={28}
        />
        <span className={mine ? "nm me" : "nm"}>
          {mine ? "我" : entry.authorUsername}
        </span>
        <span className="tm mono">{formatTime(entry.writtenAt)}</span>
        <span
          className={
            entry.visibility === "shared"
              ? "badge badge-shared"
              : "badge badge-private"
          }
        >
          {visibilityLabel(entry)}
        </span>
      </div>
      {entry.title.trim() !== "" && (
        <div className="entry-title">{entry.title}</div>
      )}
      <div className="entry-body">{entry.body.length > 0 ? excerpt(entry.body, 140) : "（无正文）"}</div>
      <div className="entry-foot">
        {entry.mood !== null && <span className="meta">{entry.mood}</span>}
        <span className="grow" />
        {read && (
          <span className="meta read">
            <CheckIcon size={14} />
            已读
          </span>
        )}
        {mine && entry.visibility === "private" && (
          <span className="lock-note">
            <LockIcon size={13} />
            只有我能看到
          </span>
        )}
      </div>
    </Link>
  );
}
